import { useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

import { scannerControl } from "@/lib/api";
import { useScanner } from "@/lib/useScanner";

/** ScannerControls is the admin-only control panel that moved out of
 *  the dashboard's ScannerPanel. Keeps the 5 actions (start/stop/
 *  refresh/reapply/reset) + runtime state in one card. Uses the
 *  same /api/scanner/stream SSE as the dashboard panel so the two
 *  stay in sync when both are open. */
export function ScannerControls() {
  const { status, progress, lastError, connecting } = useScanner(true);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionNotice, setActionNotice] = useState<string | null>(null);

  const running = status?.running ?? false;
  const universeSize = status?.universe_size ?? 0;
  const scanned = progress?.scanned ?? status?.scanned ?? 0;
  const queueDepth = progress?.queueDepth ?? status?.queue_depth ?? 0;

  const handleAction = async (
    action: "start" | "stop" | "refresh" | "reset" | "reapply",
  ) => {
    setActionError(null);
    setActionNotice(null);
    try {
      const resp = await scannerControl(action);
      if (resp.message) setActionNotice(resp.message);
    } catch (e: unknown) {
      setActionError(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CardTitle>Scanner control</CardTitle>
            <Badge variant={running ? "default" : "secondary"}>
              {running ? "running" : "stopped"}
            </Badge>
            {(progress?.circuitOpen || status?.circuit_open) && (
              <Badge variant="destructive">
                circuit open · {status?.circuit_cooldown_sec ?? 0}s
              </Badge>
            )}
            {status?.regime_gate ? (
              <Badge variant={status.regime_favorable ? "secondary" : "destructive"}>
                regime {status.regime_favorable ? "ok" : "risk-off"}
                {status.regime_benchmark ? ` · ${status.regime_benchmark}` : ""}
              </Badge>
            ) : null}
            {connecting && !status && (
              <span className="text-xs text-muted-foreground">connecting…</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {running ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => void handleAction("stop")}
              >
                Stop
              </Button>
            ) : (
              <Button size="sm" onClick={() => void handleAction("start")}>
                Start
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => void handleAction("refresh")}
              title="Re-fetch the NASDAQ Trader symbol list"
            >
              Refresh universe
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void handleAction("reapply")}
              title="Re-evaluate every cached row against the current hit gate without refetching"
            >
              Re-evaluate
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void handleAction("reset")}
              title="Clear per-ticker error counts so quarantined symbols rescan"
            >
              Reset quarantine
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-xs">
          <div>
            <div className="text-muted-foreground">Universe</div>
            <div className="font-mono">
              {universeSize.toLocaleString()} symbols
            </div>
          </div>
          <div>
            <div className="text-muted-foreground">Scanned</div>
            <div className="font-mono">
              {scanned.toLocaleString()} / {universeSize.toLocaleString()}
            </div>
          </div>
          <div>
            <div className="text-muted-foreground">Queue</div>
            <div className="font-mono">{queueDepth.toLocaleString()}</div>
          </div>
          <div>
            <div className="text-muted-foreground">Passed hits</div>
            <div className="font-mono">{status?.passed_hits ?? 0}</div>
          </div>
        </div>
        <div className="h-2 w-full overflow-hidden rounded bg-muted">
          <div
            className="h-full bg-emerald-500 transition-all"
            style={{
              width: `${universeSize > 0 ? Math.min(100, (scanned / universeSize) * 100) : 0}%`,
            }}
          />
        </div>
        {progress?.current && (
          <p className="text-xs text-muted-foreground font-mono">
            scanning: {progress.current} · last tick{" "}
            {new Date(progress.lastTickAt).toLocaleTimeString()}
          </p>
        )}
        {(actionError || lastError) && (
          <p className="text-xs text-rose-500">{actionError ?? lastError}</p>
        )}
        {actionNotice && !actionError && (
          <p className="text-xs text-muted-foreground">{actionNotice}</p>
        )}
      </CardContent>
    </Card>
  );
}
